<template>
  <div class="list-container">

    <div v-if="showSorter">
      <div class="sorter-pill-controls">
        <div class="pill-row">
          <div class="pill-toggle dark-pill" @click="toggleSortCategoryMenu">
            <span>{{ sortCategory }}</span>
            <span class="material-icons-outlined">
              {{ sortCategoryMenuOpen ? 'expand_less' : 'expand_more' }}
            </span>
          </div>
          <div class="pill-toggle" @click="toggleSortDirectionMenu">
            <span>{{ formatStatusLabel(sortDirection) }}</span>
            <span class="material-icons-outlined">
              {{ sortDirectionMenuOpen ? 'expand_less' : 'expand_more' }}
            </span>
          </div>
        </div>
        <div v-if="sortCategoryMenuOpen" class="pill-dropdown">
          <span v-for="option in sortCategories" :key="option" @click="setSortCategory(option)">
            {{ option }}
          </span>
        </div>
        <div v-if="sortDirectionMenuOpen" class="pill-dropdown">
          <div
            v-for="option in sortOptions"
            :key="option"
            @click="setSortDirection(option)"
            class="status-option"
          >
            <span class="status-label">{{ formatStatusLabel(option) }}</span>
            <span
              v-if="sortCategory === 'Status' && option !== 'All'"
              class="status-dot"
              :style="{ backgroundColor: getStatusColor(option) }"
            ></span>
          </div>
        </div>
      </div>
    </div>

    <button
  v-for="(item, index) in sortedItems"
  :key="index"
  class="list-button"
  :class="{ 'left-style': mode === 'left', 'right-style': mode === 'right' }"
  :style="getButtonStyle(item)"
  @click="emitClick(item)"
>
  <div class="button-content">
    <p class="item-title">{{ getItemTitle(item) }}</p>
    <p v-if="getItemSubtitle(item)" class="item-subtitle">
      {{ getItemSubtitle(item) }}
    </p>

    <div
      v-if="$slots['item-action'] || (showsDate && item.lastUpdated)"
      class="meta-row"
    >
      <div class="left-actions">
        <slot name="item-action" :item="item" />
      </div>

      <p v-if="showsDate && item.lastUpdated" class="item-time">
        {{ getRelativeTime(item.lastUpdated) }}
      </p>
    </div>
  </div>
</button>

  </div>
</template>

<script>
export default {
  name: "ListViewBuilder",
  props: {
    items: {
      type: Array,
      required: false,
      default: () => [],
    },
    mode: {
      type: String,
      required: true,
      validator: (value) => ["left", "right"].includes(value),
    },
    itemType: {
      type: String,
      required: true,
    },
    statusColors: {
      type: Array,
      required: false,
      default: () => [],
    },
    showSorter: {
      type: Boolean,
      default: true,
    },
    showsDate: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      sortCategory: "Time",
      sortDirection: "Most Recent First",
      sortCategories: ["Time", "Status", "State"],
      sortOptions: [],
      jurisdictions: [],
      sortCategoryMenuOpen: false,
      sortDirectionMenuOpen: false,
      statuses: [
        { label: "Submitted", color: "#999999" },
        { label: "Needs Info", color: "#FFD700" },
        { label: "In Review", color: "#87CEEB" },
        { label: "Approved", color: "#28a745" },
        { label: "Rejected", color: "#dc3545" },
        { label: "Filed", color: "#28a745aa" },
      ],
    };
  },
  computed: {
    safeItems() {
      // ALWAYS return an array
      return Array.isArray(this.items) ? this.items : [];
    },
    sortedItems() {
      const source = this.safeItems;

      // filter by Status or State if applicable
      const filtered = source.filter((item) => {
        if (this.sortCategory === "Status" && this.sortDirection !== "All") {
          return (item.caseStatus || "").toLowerCase() === this.sortDirection.toLowerCase();
        }
        if (this.sortCategory === "State" && this.sortDirection !== "All") {
          return (item.caseTitle || "").toLowerCase() === this.sortDirection.toLowerCase();
        }
        return true;
      });

      if (this.sortCategory === "Time") {
        return [...filtered].sort((a, b) => {
          const aTime = new Date(a.lastUpdated || 0).getTime();
          const bTime = new Date(b.lastUpdated || 0).getTime();
          return this.sortDirection === "Most Recent First" ? bTime - aTime : aTime - bTime;
        });
      }

      // default: no extra sort
      return filtered;
    },
  },
  watch: {
    items: {
      handler(newItems) {
        const safe = Array.isArray(newItems) ? newItems : [];
        if (this.itemType === "case") {
          const states = new Set();
          safe.forEach((item) => {
            if (item.caseTitle) states.add(item.caseTitle);
          });
          this.jurisdictions = Array.from(states).sort();
        }
        this.updateSortOptions();
      },
      immediate: true,
      deep: true,
    },
  },
  mounted() {
    this.updateSortOptions();

    if (this.itemType === "case") {
      const states = new Set();
      this.safeItems.forEach((item) => {
        if (item.caseTitle) states.add(item.caseTitle);
      });
      this.jurisdictions = Array.from(states).sort();
    }
  },
  emits: ["itemClick"],
  methods: {
    emitClick(item) {
      this.$emit("itemClick", { item, type: this.itemType });
    },
    formatStatusLabel(label) {
      if (!label) return "";
      return label.charAt(0).toUpperCase() + label.slice(1).toLowerCase();
    },
    getStatusColor(label) {
      const normalize = (s) => (s || "").toLowerCase().trim();
      const match = this.statusColors.find((s) => normalize(s.label) === normalize(label));
      return match?.color || "#ccc";
    },
    getItemTitle(item) {
      switch (this.itemType) {
        case "chat":
          return item.clientName || "Unknown Chat";
        case "case":
          return item.caseTitle || "Untitled";
        default:
          return "Unknown";
      }
    },
    getItemSubtitle(item) {
      switch (this.itemType) {
        case "chat":
          return `Topic: ${item.chatTopic || "No Topic"}`;
        case "case":
          return `Status: ${this.formatStatusLabel(item.caseStatus || "Pending")}`;
        default:
          return "";
      }
    },
    getRelativeTime(date) {
      if (!date) return "";
      const updated = new Date(date);
      if (isNaN(updated)) return "";
      const now = new Date();
      const diffMs = now - updated;
      const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
      if (diffDays === 0) return "Today";
      if (diffDays === 1) return "1 day ago";
      return `${diffDays} days ago`;
    },
    normalize(str) {
      return (str || "").toLowerCase().trim();
    },
    getButtonStyle(item) {
      if (this.itemType === "case" && Array.isArray(this.statusColors)) {
        const itemStatus = this.normalize(item.caseStatus);
        const match = this.statusColors.find((s) => this.normalize(s.label) === itemStatus);
        return {
          backgroundColor: match?.color || "#f4f4f4",
          color: match ? "#fff" : "#000",
        };
      }
      return {};
    },
    updateSortOptions() {
      if (this.sortCategory === "Time") {
        this.sortOptions = ["Most Recent First", "Oldest First"];
        if (!["Most Recent First", "Oldest First"].includes(this.sortDirection)) {
          this.sortDirection = "Most Recent First";
        }
      } else if (this.sortCategory === "Status") {
        const uniqueStatuses = Array.from(
          new Set(this.safeItems.map((i) => i.caseStatus || "Unknown"))
        );
        this.sortOptions = ["All", ...uniqueStatuses];
        if (!this.sortOptions.includes(this.sortDirection)) {
          this.sortDirection = "All";
        }
      } else if (this.sortCategory === "State") {
        this.sortOptions = ["All", ...this.jurisdictions];
        if (!this.sortOptions.includes(this.sortDirection)) {
          this.sortDirection = "All";
        }
      }
    },
    toggleSortCategoryMenu() {
      this.sortCategoryMenuOpen = !this.sortCategoryMenuOpen;
      this.sortDirectionMenuOpen = false;
    },
    toggleSortDirectionMenu() {
      this.sortDirectionMenuOpen = !this.sortDirectionMenuOpen;
      this.sortCategoryMenuOpen = false;
    },
    setSortCategory(option) {
      this.sortCategory = option;
      this.sortCategoryMenuOpen = false;
      this.updateSortOptions();
    },
    setSortDirection(option) {
      this.sortDirection = option;
      this.sortDirectionMenuOpen = false;
    },
  },
};
</script>
  
<style scoped>

.button-content {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.item-time {
  font-size: 12px;
  color: #555;
  margin-top: 2px;
  align-self: flex-end;
}

/* Optional: Responsive tweak */
@media (max-width: 600px) {
  .item-time {
    align-self: flex-start;
  }
}


.sorter-pill-controls {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 12px;
  gap: 8px;
  position: relative;
}

.pill-row {
  display: flex;
  justify-content: center;
  gap: 12px;
  flex-wrap: wrap;
}

.pill-toggle {
  display: flex;
  align-items: center;
  background: grey;
  color: white;
  padding: 10px 20px;
  border-radius: 24px;
  cursor: pointer;
  transition: opacity 0.2s;
  width: 200px;
  justify-content: space-between;
  white-space: nowrap;
  font-weight: 500;
}

.pill-toggle.dark-pill {
  background: #002f5f;
  color: white;
}

.pill-dropdown {
  position: absolute;
  top: 100%;
  margin-top: 4px;
  border: 1px solid #ccc;
  border-radius: 12px;
  padding: 8px;
  width: 420px;
  z-index: 10;
  backdrop-filter: blur(10px);
  background-color: #002f5f80;
}

.pill-dropdown span {
  display: block;
  padding: 6px;
  cursor: pointer;
  border-radius: 8px;
  transition: background 0.2s;
  color: white;
}

.pill-dropdown span:hover {
  background: #f0f0f0;
  color: black;
}

.status-option {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 6px;
  cursor: pointer;
  border-radius: 8px;
  color: white;
  transition: background 0.2s;
}

.status-option:hover {
  background: #f0f0f0;
  color: black;
}

.status-label {
  flex: 1;
  white-space: nowrap;
}

.status-dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  margin-left: 12px;
  flex-shrink: 0;
}

.pill-toggle span {
  text-transform: none !important;
}

.button-content {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  text-align: left;
}

/* Footer row: button (slot) on the left, date on the right */
.meta-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  width: 100%;
  margin-top: 12px;
}

/* Ensure the slot content doesn't collapse */
.left-actions :deep(*) {
  display: inline-flex;
}

/* White date text, aligned with the button */
.item-time {
  margin: 0;
  font-size: 12px;
  color: #ffffff;          /* <- white */
  opacity: 0.9;            /* optional: soften white a bit */
}

</style>